/**
 * useActivityLog - Composable for activity log and chat functionality
 *
 * Extracted from GameBoard.vue to reduce component complexity.
 * Handles:
 * - Activity log entries (game events)
 * - Chat messages and history
 * - Emoji/GIF picker state
 * - Sidebar tab switching
 *
 * Usage:
 * ```typescript
 * const activityLog = useActivityLog()
 * activityLog.setup({ currentPlayer, socket, giphy })
 * ```
 */

import { ref, nextTick, type ComputedRef } from 'vue'
import type { ChatMessagePayload, ChatHistoryPayload, ChatGifData } from './useSocket'
import type { Card } from './useCards'

// ============================================================================
// Types
// ============================================================================

/** Activity log entry */
export interface ActivityEntry {
  playerName: string
  action: string
  cardInfo?: string
  isMe: boolean
  isPartner: boolean
  isSystem: boolean
  seatPosition?: number
  timestamp: number
}

/** Player type - minimal interface */
interface PlayerLike {
  seat_position?: number
}

/** Options for logActivity function */
interface LogActivityOptions {
  isMe?: boolean
  isPartner?: boolean
  isSystem?: boolean
  seatPosition?: number
  cardInfo?: string
}

/** Setup options for configuring the composable */
interface UseActivityLogOptions {
  /** Current player computed ref */
  currentPlayer: ComputedRef<PlayerLike | undefined>
  /** Socket composable for sending chat */
  socket: {
    sendChatMessage: (message: string, gif?: ChatGifData) => void
  }
  /** Giphy composable for URL validation */
  giphy: {
    isValidGiphyUrl: (url: string) => boolean
  }
  /** Optional logger function */
  logger?: {
    warn: (...args: unknown[]) => void
  }
}

// ============================================================================
// Constants
// ============================================================================

const MAX_ACTIVITY_ENTRIES = 50
const MAX_CHAT_MESSAGES = 100

// ============================================================================
// Composable
// ============================================================================

export function useActivityLog() {
  // External dependencies - stored via setup()
  let deps: UseActivityLogOptions | null = null

  // ============================================================================
  // Activity Log State
  // ============================================================================

  /** Activity log entries */
  const activityLog = ref<ActivityEntry[]>([])

  // ============================================================================
  // Chat State
  // ============================================================================

  /** Chat messages from server */
  const chatMessages = ref<ChatMessagePayload[]>([])

  /** Chat message input */
  const chatInput = ref('')

  /** Chat messages container ref for auto-scroll */
  const chatMessagesRef = ref<HTMLElement | null>(null)

  /** Active tab in sidebar: 'activity' or 'chat' */
  const sidebarTab = ref<'activity' | 'chat'>('activity')

  /** Show emoji picker dropdown */
  const showEmojiPicker = ref(false)

  /** Show GIF picker dropdown */
  const showGifPicker = ref(false)

  /** Pending GIF to send with message */
  const pendingGif = ref<ChatGifData | null>(null)

  /** Flag to prevent double-sends */
  let isSending = false

  // ============================================================================
  // Activity Log Functions
  // ============================================================================

  /**
   * Add activity log entry with full player info
   */
  function logActivity(
    playerName: string,
    action: string,
    options: LogActivityOptions = {}
  ): void {
    const entry: ActivityEntry = {
      playerName,
      action,
      cardInfo: options.cardInfo,
      isMe: options.isMe ?? false,
      isPartner: options.isPartner ?? false,
      isSystem: options.isSystem ?? false,
      seatPosition: options.seatPosition,
      timestamp: Date.now(),
    }

    activityLog.value.push(entry)

    // Keep only last N entries
    if (activityLog.value.length > MAX_ACTIVITY_ENTRIES) {
      activityLog.value = activityLog.value.slice(-MAX_ACTIVITY_ENTRIES)
    }
  }

  /**
   * Simple helper for local actions (backwards compatible)
   */
  function addActivityMessage(message: string): void {
    const myPlayer = deps?.currentPlayer?.value
    logActivity('You', message, {
      isMe: true,
      seatPosition: myPlayer?.seat_position,
    })
  }

  /**
   * Get CSS class for activity log player name based on seat position
   */
  function getActivityPlayerClass(entry: ActivityEntry): string {
    if (entry.isSystem) return ''
    if (entry.seatPosition !== undefined) {
      return `seat-${entry.seatPosition}`
    }
    if (entry.isMe) return 'seat-0'
    if (entry.isPartner) return 'seat-2'
    return ''
  }

  /**
   * Format card for activity log display
   */
  function formatCardForLog(card: Card): string {
    if (!card) return ''
    if (card.rank === 'JOKER') return 'Joker'
    const suitSymbols: Record<string, string> = {
      hearts: '♥',
      diamonds: '♦',
      clubs: '♣',
      spades: '♠',
      HEARTS: '♥',
      DIAMONDS: '♦',
      CLUBS: '♣',
      SPADES: '♠',
    }
    return `${card.rank}${suitSymbols[card.suit] || card.suit}`
  }

  /**
   * Clear activity log (e.g., on new round)
   */
  function clearActivityLog(): void {
    activityLog.value = []
  }

  // ============================================================================
  // Chat Functions
  // ============================================================================

  /**
   * Send a chat message
   */
  function sendChat(): void {
    // Prevent double-sends
    if (isSending) return

    const message = chatInput.value.trim()
    const gif = pendingGif.value

    // Need either a message or a GIF to send
    if (!message && !gif) return

    // Set flag and clear inputs immediately
    isSending = true
    chatInput.value = ''
    pendingGif.value = null

    deps?.socket?.sendChatMessage(message, gif || undefined)

    // Reset flag after short delay
    setTimeout(() => {
      isSending = false
    }, 100)
  }

  /**
   * Format chat timestamp (HH:MM)
   */
  function formatChatTime(timestamp: number): string {
    const date = new Date(timestamp * 1000)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  /**
   * SECURITY: Get validated GIF URL or empty string
   * Prevents rendering of GIFs from non-Giphy sources
   */
  function getSafeGifUrl(url: string | undefined): string {
    if (!url) return ''
    if (!deps?.giphy?.isValidGiphyUrl(url)) {
      deps?.logger?.warn('SECURITY: Rejected invalid GIF URL:', url)
      return ''
    }
    return url
  }

  /**
   * Scroll chat to bottom
   */
  function scrollChatToBottom(): void {
    nextTick(() => {
      const container = chatMessagesRef.value
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })
  }

  /**
   * Handle incoming chat message
   */
  function handleChatMessage(data: ChatMessagePayload): void {
    // Deduplicate by message ID to prevent duplicate handlers
    if (chatMessages.value.some(msg => msg.id === data.id)) {
      return
    }
    chatMessages.value.push(data)
    // Keep last N messages
    if (chatMessages.value.length > MAX_CHAT_MESSAGES) {
      chatMessages.value = chatMessages.value.slice(-MAX_CHAT_MESSAGES)
    }
    scrollChatToBottom()
  }

  /**
   * Handle chat history on reconnection
   */
  function handleChatHistory(data: ChatHistoryPayload): void {
    chatMessages.value = data.messages || []
    scrollChatToBottom()
  }

  /**
   * Toggle emoji picker visibility
   */
  function toggleEmojiPicker(): void {
    showEmojiPicker.value = !showEmojiPicker.value
    // Close GIF picker if open
    if (showEmojiPicker.value) {
      showGifPicker.value = false
    }
  }

  /**
   * Toggle GIF picker visibility
   */
  function toggleGifPicker(): void {
    showGifPicker.value = !showGifPicker.value
    // Close emoji picker if open
    if (showGifPicker.value) {
      showEmojiPicker.value = false
    }
  }

  /**
   * Insert emoji into chat input
   */
  function insertEmoji(emoji: string): void {
    chatInput.value += emoji
    showEmojiPicker.value = false
  }

  /**
   * Stage a GIF for sending (doesn't send immediately)
   */
  function stageGif(gif: ChatGifData): void {
    pendingGif.value = gif
    showGifPicker.value = false
  }

  /**
   * Clear the pending GIF
   */
  function clearPendingGif(): void {
    pendingGif.value = null
  }

  // ============================================================================
  // Setup Function
  // ============================================================================

  /**
   * Configure the composable with external dependencies
   */
  function setup(options: UseActivityLogOptions): void {
    deps = options
  }

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Activity Log State
    activityLog,

    // Chat State
    chatMessages,
    chatInput,
    chatMessagesRef,
    sidebarTab,
    showEmojiPicker,
    showGifPicker,
    pendingGif,

    // Activity Log Functions
    logActivity,
    addActivityMessage,
    getActivityPlayerClass,
    formatCardForLog,
    clearActivityLog,

    // Chat Functions
    sendChat,
    formatChatTime,
    getSafeGifUrl,
    scrollChatToBottom,
    handleChatMessage,
    handleChatHistory,
    toggleEmojiPicker,
    toggleGifPicker,
    insertEmoji,
    stageGif,
    clearPendingGif,

    // Setup
    setup,
  }
}
